
import { Component, Mixins, Provide, Prop } from "vue-property-decorator";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { DataModules } from "@/store/modules/data";
import { ITag } from "@/models/tags";
import { UUID } from "@/data/enums";
import { IBrand } from "@/models/brands";
import { CreateElement } from "vue";
import { ITagsProvider, ITagsProviderMethods } from "@/models/providers/tags";
import { ITableConfig } from "@/models/tables";
import { Sorters } from "@/data/table";

@Component
export default class TagsProvider extends Mixins(FilteringSortingTableStore) {
  // Props
  @Prop({ default: "TP" })
  public declare id: string;

  @Prop({ default: () => () => true })
  public predicateAvailable!: (tag: ITag) => boolean;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public declare manualFilters: Record<string, string | number | boolean>;

  @Prop({ default: UUID.ORG })
  public brandId!: IBrand["id"];

  @Prop({ default: false })
  public withStagedImports!: boolean;

  @Prop({ default: 0 })
  public declare limit: number;

  // Data
  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {
      field: "name",
      direction: Sorters.DESCENDING
    },
    filters: [],
    constFilters: []
  };

  public dataModule = DataModules.TAGS;
  public isReloading = false;

  // Life-cycle methods
  public created() {
    this.loadData();
  }

  // Computed

  public get hasTags(): boolean {
    return !!this.availableTags.length;
  }

  public get canCreate() {
    return this.$userCan("create_tag");
  }

  public get canUpdate() {
    return this.$userCan("update_tag");
  }

  public get canDelete() {
    return this.$userCan("delete_tag");
  }

  public get canManage() {
    return this.canCreate || this.canUpdate || this.canDelete;
  }

  public get tags() {
    return this.brandId !== UUID.ORG
      ? (this.table.data as ITag[]).filter(tag => tag.brand_id === this.brandId)
      : (this.table.data as ITag[]);
  }

  public get availableTags() {
    return this.tags?.filter(this.predicateAvailable);
  }

  public get brandFilter() {
    return this.brandId !== UUID.ORG
      ? { "filter[brand_id]": this.brandId }
      : {};
  }

  // Methods

  public loadData() {
    this.getData();
  }

  public async reloadData() {
    this.isReloading = true;
    this.cancelRequests();
    return this.getData({ ignoreStored: true }).finally(() => {
      this.isReloading = false;
    });
  }

  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          ...(this.withStagedImports ? { with_staged_imports: 1 } : {}),
          ...this.manualFilters
        }
      },
      params
    );
  }

  public openSelectTagsModal({ activeTags }: { activeTags: ITag[] }) {
    return new Promise<ITag[]>(resolve => {
      this.$store.dispatch(`data/${DataModules.TAGS}/openSelectModal`, {
        canCancel: ["escape", "outside", "x", "button"],
        props: {
          brandId: this.brandId,
          activeTags
        },
        events: {
          success: tags => {
            resolve(tags);
            this.$emit("tags-select-many", tags);
          }
        }
      });
    });
  }

  public openManageModal() {
    this.$store.dispatch(`data/${DataModules.TAGS}/openManageModal`, {
      events: {
        delete: tag => {
          this.$emit("delete", tag);
          this.$emit("save");
        },
        update: () => {
          this.reloadData();
          this.$emit("save");
        }
      }
    });
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.TAGS,
      scope: this.dataScope,
      vm: this
    });
  }

  @Provide("tagsMethods")
  public get providerMethods(): ITagsProviderMethods {
    return {
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData,
      openManageModal: this.openManageModal,
      openSelectTagsModal: this.openSelectTagsModal
    };
  }

  @Provide("tagsProvider")
  public providerData(): ITagsProvider {
    return {
      brandId: this.brandId,
      hasTags: this.hasTags,
      isLoading: this.table.loading,
      limit: this.table.limit,
      page: this.table.page,
      tags: this.tags,
      availableTags: this.availableTags,
      table: this.table,
      total: this.table.total,
      isReloading: this.isReloading,
      canCreate: this.canCreate,
      canUpdate: this.canUpdate,
      canDelete: this.canDelete,
      canManage: this.canManage
    };
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.isReloading && this.requestError },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $tagsData: this.providerData(),
        $tagsMethods: this.providerMethods
      })
    );
  }
}
